import { useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 2 * 60 * 1000;
// Require this many consecutive mismatches before prompting, to absorb a
// transient stale response mid-deploy.
const MISMATCH_THRESHOLD = 2;

async function fetchCurrentBuildId(): Promise<string | null> {
  try {
    // `cache: 'no-store'` only bypasses the browser cache; append a unique
    // query param so the request also misses any CDN edge cache and reaches
    // origin, returning the true current build.
    const href = window.location.href;
    const url = `${href}${href.includes('?') ? '&' : '?'}_vc=${Date.now()}`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return null;
    const text = await res.text();
    const match = text.match(/"buildId":"([^"]+)"/);
    return match ? match[1] : null;
  } catch (err) {
    return null;
  }
}

export function useVersionCheck() {
  const initialId = useRef<string | null>(null);
  const mismatchCount = useRef(0);
  const [isOutdated, setIsOutdated] = useState(false);

  useEffect(() => {
    initialId.current = (window as any).__NEXT_DATA__?.buildId ?? null;

    const interval = setInterval(async () => {
      const current = await fetchCurrentBuildId();
      if (current && initialId.current && current !== initialId.current) {
        mismatchCount.current += 1;
        if (mismatchCount.current >= MISMATCH_THRESHOLD) {
          setIsOutdated(true);
          clearInterval(interval);
        }
      } else {
        // A match (or a failed/empty poll) resets the streak.
        mismatchCount.current = 0;
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  return isOutdated;
}
